import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { PersonService } from '../services/person.service';
import { Firestore, collection, query, where, getDocs } from '@angular/fire/firestore';
import { Result } from '../models/result.interface';

@Component({
  selector: 'app-trainer-show-results',
  templateUrl: './trainer-show-results.component.html',
  styleUrls: ['./trainer-show-results.component.scss']
})
export class TrainerShowResultsComponent implements OnInit {
  public trainerId: string = '';
  public showId: string = '';
  public trainerInfo: any = null;
  public showInfo: any = null;
  public results: Result[] = [];
  public filteredResults: Result[] = [];
  public isLoading: boolean = true;
  public error: string | null = null;
  public prefix: string = '';
  public classEntryCounts: Map<string, number> = new Map();

  // Statistics
  public totalEntries: number = 0;
  public wins: number = 0;
  public podiums: number = 0;

  // Sorting
  public sortColumn: string = 'date';
  public sortDirection: 'asc' | 'desc' = 'desc';

  constructor(
    private route: ActivatedRoute,
    private titleService: Title,
    private metaService: Meta,
    private personService: PersonService,
    private firestore: Firestore
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.trainerId = params['trainerId'] || '';
      this.showId = params['showId'] || '';
      
      if (this.trainerId && this.showId) {
        this.loadData();
      } else {
        this.error = 'Invalid trainer or show ID';
        this.isLoading = false;
      }
    });
  }

  private async loadData(): Promise<void> {
    try {
      this.isLoading = true;
      this.error = null;

      // Determine if this is US data
      this.prefix = this.trainerId.includes('us_') ? 'us_' : '';

      // Load trainer info, show info, and results in parallel
      await Promise.all([
        this.loadTrainerInfo(),
        this.loadShowInfo(),
        this.loadResults()
      ]);

      this.calculateStatistics();
      await this.loadClassEntryCounts();
      this.updatePageMetadata();
      
    } catch (error) {
      console.error('Error loading data:', error);
      this.error = 'Error loading trainer and show results';
    } finally {
      this.isLoading = false;
    }
  }

  private async loadTrainerInfo(): Promise<void> {
    try {
      // For now, we'll try to get trainer info from the person service
      // This might need to be adapted based on how trainer data is stored
      const response = await this.personService.getPerson(this.trainerId.replace('us_', ''), this.showId.replace('us_', '')).toPromise();
      this.trainerInfo = response?.data?.personInfo || null;
    } catch (error) {
      console.log('Could not load trainer info, will try to extract from results');
    }
  }

  private async loadShowInfo(): Promise<void> {
    try {
      // Load show information from Firestore
      const showDoc = await getDocs(query(
        collection(this.firestore, 'concursos'),
        where('__name__', '==', this.showId)
      ));
      
      if (!showDoc.empty) {
        this.showInfo = showDoc.docs[0].data();
      }
    } catch (error) {
      console.log('Could not load show info');
    }
  }

  private async loadResults(): Promise<void> {
    console.log('Loading results for trainer:', this.trainerId, 'and show:', this.showId);
    try {
      // Query results by trainer and show
      const resultsRef = collection(this.firestore, 'results');
      const q = query(
        resultsRef,
        where('trainerId', '==', this.trainerId),
        where('showId', '==', this.showId)
      );
      
      const querySnapshot = await getDocs(q);
      this.results = querySnapshot.docs.map(doc => doc.data() as Result);
      this.filteredResults = [...this.results];
      
      // If no results found with trainerId, try with riderId (in case trainer data is stored differently)
      if (this.results.length === 0) {
        const riderQuery = query(
          resultsRef,
          where('riderId', '==', this.trainerId),
          where('showId', '==', this.showId)
        );
        
        const riderSnapshot = await getDocs(riderQuery);
        this.results = riderSnapshot.docs.map(doc => doc.data() as Result);
        this.filteredResults = [...this.results];
      }
      
      // Extract trainer info from results if not found elsewhere
      if (!this.trainerInfo && this.results.length > 0) {
        const firstResult = this.results[0];
        this.trainerInfo = {
          name: firstResult.riderName || 'Unknown Trainer',
          // Add other fields as available
        };
      }
      
    } catch (error) {
      console.error('Error loading results:', error);
      throw error;
    }
  }

  private calculateStatistics(): void {
    this.totalEntries = this.results.length;
    this.wins = this.results.filter(r => r.rank === '1').length;
    this.podiums = this.results.filter(r => ['1', '2', '3'].includes(r.rank)).length;
  }

  private updatePageMetadata(): void {
    const trainerName = this.trainerInfo?.name || 'Trainer';
    const showName = this.showInfo?.nombre || 'Show';
    const pageTitle = `${trainerName} - ${showName} Results | ED`;
    const description = `Competition results for ${trainerName} at ${showName}. View detailed performance data and statistics.`;

    this.titleService.setTitle(pageTitle);
    this.metaService.updateTag({ name: 'description', content: description });
    this.metaService.updateTag({ property: 'og:title', content: pageTitle });
    this.metaService.updateTag({ property: 'og:description', content: description });
  }

  public sortResults(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'desc';
    }
    this.applySort();
  }

  private applySort(): void {
    this.filteredResults.sort((a, b) => {
      let aVal: any, bVal: any;
      
      switch (this.sortColumn) {
        case 'date':
          aVal = new Date(a.date);
          bVal = new Date(b.date);
          break;
        case 'rank':
          aVal = parseInt(a.rank) || 999;
          bVal = parseInt(b.rank) || 999;
          break;
        case 'class':
          aVal = a.className;
          bVal = b.className;
          break;
        case 'rider':
          aVal = a.riderName;
          bVal = b.riderName;
          break;
        case 'horse':
          aVal = a.horseName || a.horse;
          bVal = b.horseName || b.horse;
          break;
        case 'owner':
          aVal = a.owner || '';
          bVal = b.owner || '';
          break;
        default:
          aVal = a[this.sortColumn];
          bVal = b[this.sortColumn];
      }

      if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  public formatDate(dateValue: string | any): string {
    try {
      let date: Date;
      
      // Handle Firestore Timestamp objects
      if (dateValue && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      } 
      // Handle string dates
      else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      }
      // Handle Date objects
      else if (dateValue instanceof Date) {
        date = dateValue;
      }
      else {
        return dateValue?.toString() || '';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateValue?.toString() || '';
    }
  }

  private async loadClassEntryCounts(): Promise<void> {
    try {
      // Get unique competition IDs from our results
      const competitionIds = [...new Set(this.results.map(r => r.competitionId || r.ipc))];
      
      for (const competitionId of competitionIds) {
        if (competitionId) {
          // Query all results for this competition to count entries
          const resultsRef = collection(this.firestore, 'results');
          const q = query(
            resultsRef,
            where('competitionId', '==', competitionId)
          );
          
          try {
            const querySnapshot = await getDocs(q);
            this.classEntryCounts.set(competitionId, querySnapshot.docs.length);
          } catch (error) {
            // Try with ipc field if competitionId doesn't work
            const qIpc = query(
              resultsRef,
              where('ipc', '==', competitionId)
            );
            const ipcSnapshot = await getDocs(qIpc);
            this.classEntryCounts.set(competitionId, ipcSnapshot.docs.length);
          }
        }
      }
    } catch (error) {
      console.log('Could not load class entry counts:', error);
    }
  }

  public getEntryCount(result: Result): string {
    const competitionId = result.competitionId || result.ipc;
    const count = this.classEntryCounts.get(competitionId);
    return count ? count.toString() : '-';
  }

  public downloadCSV(): void {
    if (this.filteredResults.length === 0) {
      console.log('No results to export');
      return;
    }

    // Prepare CSV headers (matching the table columns)
    const headers = [
      'Date',
      'Class',
      'Rank',
      'Rider',
      'Entry #',
      'Horse',
      'Owner',
      'Entries',
      'Result',
      'Prize'
    ];

    // Convert results to CSV rows
    const rows = this.filteredResults.map(result => [
      this.formatDate(result.inicio),
      result.className || '',
      result.rank || '',
      result.riderName || '',
      result.entryNumber || result.number || '',
      result.horseName || result.horse || '',
      result.owner || '',
      this.getEntryCount(result),
      this.formatResult(result),
      result.prize || ''
    ]);

    // Combine headers and rows
    const csvContent = [headers, ...rows]
      .map(row => row.map(field => this.escapeCsvField(field)).join(','))
      .join('\n');

    // Generate filename
    const trainerName = this.trainerInfo?.name || 'Trainer';
    const showName = this.showInfo?.nombre || 'Show';
    const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const filename = `${trainerName}-${showName}-Results-${timestamp}.csv`;

    // Create and download the file
    this.downloadFile(csvContent, filename, 'text/csv');
  }

  private formatResult(result: Result): string {
    const parts = [];
    
    if (result.result1 && result.result1 != '0.000' && result.result1 != '0.00') {
      parts.push(result.result1);
    }
    if (result.result2 && result.result2 != '0.000' && result.result2 != '0.00') {
      parts.push(result.result2);
    }
    
    return parts.join(' - ') || result.rankCat || '';
  }

  private escapeCsvField(field: string): string {
    if (!field) return '';
    
    // Convert to string and handle special characters
    const str = field.toString();
    
    // If field contains comma, newline, or quotes, wrap in quotes and escape internal quotes
    if (str.includes(',') || str.includes('\n') || str.includes('"')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    
    return str;
  }

  private downloadFile(content: string, filename: string, contentType: string): void {
    const blob = new Blob([content], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL object
    window.URL.revokeObjectURL(url);
  }
} 